<template>
  <div>
    <div class="row mb-3">
      <div class="col-md-9">
        <div class="alert" :class="solicitud.estado === 'RECHAZADA' ? 'alert-danger' : 'alert-info'">
          <strong>Estado actual:</strong> {{ formatEstado(solicitud.estado) }}
          <p v-if="solicitud.observacion_rechazo" class="mt-2 mb-0"><strong>Observación:</strong> {{ solicitud.observacion_rechazo }}</p>
        </div>  
      </div>
      <div class="col-md-3">
        <div class="alert">
          <BButton 
            v-if="solicitud.estado_aprobacion == 'AUTORIZADA'"
            variant="danger" 
            size="sm"
            @click="generarPdfAutorizacion"
            :disabled="generandoPdf"
            title="Generar Memorándum de Autorización RPA"
          >
            <i v-if="generandoPdf" class="fa fa-spinner fa-spin"></i>
            <i v-else class="fa fa-file-pdf"></i> 
            Autorización RPA
          </BButton>
        </div>  
      </div>
    </div>

    <!-- Cuando está en autorización -->
    <div class="row" v-if="solicitud.estado === 'EN_AUTORIZACION_RPA' && hasRoleRPA()">
      <div class="col-md-12">
        <div class="card">
          <div class="card-header bg-light"><h6 class="mb-0">Autorización de Inicio del Proceso</h6></div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-12">
                <p><strong>Documentación revisada:</strong> Se ha verificado la documentación de la solicitud.</p>
                <div class="mb-3">
                  <label class="form-label">Observación (requerido si rechaza)</label>
                  <textarea v-model="observacion" class="form-control" rows="2"></textarea>
                </div>
                <BButton variant="success" @click="procesar('autorizar')" class="me-2">
                  <i class="fa fa-check-circle me-1"></i> Autorizar Inicio del Proceso
                </BButton>
                <BButton variant="danger" @click="procesar('rechazar')">
                  <i class="fa fa-times-circle me-1"></i> Rechazar Solicitud
                </BButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Información cuando ya fue autorizada -->
    <div class="row" v-if="solicitud.estado === 'AUTORIZADA'">
      <div class="col-md-12">
        <div class="alert alert-success">
          <i class="fa fa-check-circle me-2"></i>
          <strong>Solicitud autorizada</strong> - El proceso de contratación ha sido autorizado por el RPA.
          Proceda a la siguiente etapa: Certificación POA/PAC.
        </div>
      </div>
    </div>

    <!-- Bitácora -->
    <div class="row mt-3">
      <div class="col-md-12">
        <div class="card">
          <div class="card-header bg-light"><h6 class="mb-0">Bitácora del Proceso</h6></div>
          <div class="card-body">
            <div class="table-responsive">
              <table class="table table-sm">
                <thead><tr><th>Fecha</th><th>Usuario</th><th>Acción</th><th>Detalle</th></tr></thead>
                <tbody>
                  <tr v-for="item in bitacora" :key="item.id">
                    <td>{{ formatDateTime(item.fecha_registro) }}</td>
                    <td>{{ item.usuario_registro }}</td>
                    <td><BBadge :variant="getAccionVariant(item.accion)">{{ item.accion }}</BBadge></td>
                    <td>{{ item.detalle || '-' }}</td>
                  </tr>
                  <tr v-if="bitacora.length === 0"><td colspan="4" class="text-center">No hay registros en la bitácora</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import Swal from 'sweetalert2';
import { BButton, BBadge } from 'bootstrap-vue-3';
import { useAuthStore } from '@/stores/authStore';

const props = defineProps({ solicitud: { type: Object, required: true } });
const emit = defineEmits(['refresh']);

const authStore = useAuthStore();
const observacion = ref('');
const bitacora = ref([]);
const generandoPdf = ref(false);
const hasRoleRPA = () => authStore.hasRole('RPA');

const procesar = async (accion) => {
  if (accion === 'rechazar' && !observacion.value) {
    Swal.fire('Error', 'Debe proporcionar una observación al rechazar', 'error');
    return;
  }

  const result = await Swal.fire({
    title: accion === 'autorizar' ? '¿Autorizar el inicio del proceso?' : '¿Rechazar la solicitud?',
    text: accion === 'autorizar' ? 'Esta acción dará inicio formal al proceso de contratación.' : 'La solicitud será devuelta a la unidad solicitante para correcciones.',
    icon: 'question', showCancelButton: true,
    confirmButtonText: accion === 'autorizar' ? 'Sí, autorizar' : 'Sí, rechazar'
  });

  if (result.isConfirmed) {
    try {
      await axios.post(`/autorizacion/${props.solicitud.id}`, {
        accion: accion, observacion: observacion.value
      });
      Swal.fire('Éxito', accion === 'autorizar' ? 'Proceso autorizado' : 'Solicitud rechazada', 'success');
      // emit('refresh');
      // emit('refresh', response.data.data);
      emit('refresh');
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Error al procesar', 'error');
    }
  }
};

/**
 * Generar PDF del Memorándum/Proveído de Autorización RPA
 */
const generarPdfAutorizacion = async () => {
  generandoPdf.value = true;
  try {
    const response = await axios.get(`/autorizacion/pdf/${props.solicitud.id}`, {
      responseType: 'blob'
    });
    
    // Crear URL del blob y descargar
    const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    const nombreArchivo = `Autorizacion_RPA_${props.solicitud.codigo}.pdf`;
    
    link.href = blobUrl;
    link.setAttribute('download', nombreArchivo);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
    
    Swal.fire('Éxito', 'Memorándum de autorización generado correctamente', 'success');
  } catch (error) {
    console.error('Error al generar PDF:', error);
    Swal.fire('Error', error.response?.data?.message || 'No se pudo generar el PDF', 'error');
  } finally {
    generandoPdf.value = false;
  }
};

const fetchBitacora = async () => {
  try {
    const response = await axios.get(`/solicitud-contratacion/mostrar/${props.solicitud.id}`);
    if (response.data.data.bitacora) bitacora.value = response.data.data.bitacora;
  } catch (error) { console.error(error); }
};

const formatDateTime = (date) => date ? new Date(date).toLocaleString('es-BO') : 'N/A';
const formatEstado = (e) => e.replace(/_/g, ' ').toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
const getAccionVariant = (a) => {
  if (a.includes('AUTORIZADA')) return 'success';
  if (a.includes('RECHAZADA')) return 'danger';
  if (a.includes('ENVIADO')) return 'warning';
  return 'secondary';
};

onMounted(() => { fetchBitacora(); });
</script>